import { ReactNode } from 'react';
import { Youtube } from 'lucide-react';
import { cn } from '@/lib/utils';

const SKILL_STEPS: Record<string, number> = {
  Beginner: 1,
  Advanced: 2,
  Expert: 3,
};

const SKILL_TONE: Record<string, string> = {
  Beginner: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Advanced: 'border-amber-200 bg-amber-50 text-amber-700',
  Expert: 'border-rose-200 bg-rose-50 text-rose-700',
};

/** Each cell carries its own boundary, so a row that wraps to a second line
 * never strands a separator with nothing after it. */
const CELL =
  'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm font-medium leading-none';

const TEXT_CELL =
  'max-w-full border-border/70 bg-muted/40 text-muted-foreground';

const ICON_CELL = 'border-border/70 bg-muted/40 px-1.5 transition-colors';

const BAR_HEIGHTS = ['h-[5px]', 'h-[8px]', 'h-[11px]'];

/** Difficulty is ordinal, so the bars read before the word does. */
function SkillMeter({ level }: { level: string }) {
  const steps = SKILL_STEPS[level] ?? 0;
  return (
    <span aria-hidden className="flex items-end gap-[2px]">
      {BAR_HEIGHTS.map((h, i) => (
        <span
          key={h}
          className={cn(
            'w-[3px] rounded-sm bg-current',
            h,
            i < steps ? 'opacity-100' : 'opacity-25'
          )}
        />
      ))}
    </span>
  );
}

/** Only what has been answered. A listing does not carry an empty row — an em
 * dash in every cell said "this project has no material" when it meant "you
 * haven't said yet". */
// Complexity is one branch per optional cell, as in JobSettingsCard.
// fallow-ignore-next-line complexity
export default function PostMeta({
  skillLevel,
  material,
  category,
  youtubeUrl,
  className,
}: {
  skillLevel?: string;
  material?: string;
  category?: string;
  youtubeUrl?: string | null;
  className?: string;
}) {
  const cells: ReactNode[] = [];

  if (skillLevel) {
    cells.push(
      <span
        key="skill"
        title={`Skill level: ${skillLevel}`}
        className={cn(
          CELL,
          SKILL_TONE[skillLevel] ?? 'border-border/70 text-foreground'
        )}
      >
        <SkillMeter level={skillLevel} />
        <span className="sr-only">Skill level</span>
        {skillLevel}
      </span>
    );
  }

  [
    { key: 'material', label: 'Material', value: material },
    { key: 'category', label: 'Category', value: category },
  ].forEach(({ key, label, value }) => {
    if (!value) return;
    cells.push(
      <span
        key={key}
        title={`${label}: ${value}`}
        className={cn(CELL, TEXT_CELL)}
      >
        <span className="sr-only">{label}</span>
        <span className="truncate">{value}</span>
      </span>
    );
  });

  if (youtubeUrl) {
    cells.push(
      <a
        key="youtube"
        href={youtubeUrl}
        target="_blank"
        rel="noreferrer"
        title="Watch the project video"
        aria-label="Watch the project video"
        className={cn(CELL, ICON_CELL, 'text-red-600 hover:bg-red-50')}
      >
        <Youtube className="h-[18px] w-[18px]" />
      </a>
    );
  }

  if (cells.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {cells}
    </div>
  );
}
